import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { CalendarClock, History, Pencil, Play, Plus, Trash2 } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { Schedule } from "@/api/generated/Schedule"
import { useDeleteSchedule, useRunSchedule, useSchedules, useToggleSchedule } from "@/api/queries/schedules"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ExecutionHistorySheet } from "@/features/schedules/execution-history"
import { ScheduleSheet } from "@/features/schedules/schedule-sheet"
import { useScheduleSummary } from "@/features/schedules/summary"
import { fire } from "@/features/studio/fire"

interface SchedulesSearch {
  /** Schedule open in the editor (`new` = create). */
  edit?: number | "new"
  /** Schedule whose execution history is open. */
  history?: number
}

const posInt = (v: unknown) => {
  const n = Number(v)
  return Number.isInteger(n) && n > 0 ? n : undefined
}

export const Route = createFileRoute("/_app/studio/schedules")({
  staticData: { titleKey: "nav.items.schedules" },
  validateSearch: (s: Record<string, unknown>): SchedulesSearch => ({
    edit: s.edit === "new" ? "new" : posInt(s.edit),
    history: posInt(s.history),
  }),
  component: SchedulesPage,
})

function SchedulesPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const schedules = useSchedules()
  const setSearch = (patch: Partial<SchedulesSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const editing =
    typeof search.edit === "number" ? schedules.data?.find((s) => s.id === search.edit) : undefined
  const historyOf = schedules.data?.find((s) => s.id === search.history)

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.schedules.title")}
        description={t("studio.schedules.description")}
        icon={CalendarClock}
        actions={
          <Button onClick={() => setSearch({ edit: "new" })} data-testid="new-schedule">
            <Plus />
            {t("studio.schedules.new")}
          </Button>
        }
      />
      <SchedulesTable
        schedules={schedules}
        onEdit={(s) => setSearch({ edit: s.id })}
        onHistory={(s) => setSearch({ history: s.id })}
        onCreate={() => setSearch({ edit: "new" })}
      />
      <ScheduleSheet
        open={search.edit === "new" || editing != null}
        onOpenChange={(o) => !o && setSearch({ edit: undefined })}
        schedule={search.edit === "new" ? null : (editing ?? null)}
      />
      <ExecutionHistorySheet
        open={historyOf != null}
        onOpenChange={(o) => !o && setSearch({ history: undefined })}
        schedule={historyOf}
      />
    </PageContainer>
  )
}

function SchedulesTable({
  schedules,
  onEdit,
  onHistory,
  onCreate,
}: {
  schedules: ReturnType<typeof useSchedules>
  onEdit: (s: Schedule) => void
  onHistory: (s: Schedule) => void
  onCreate: () => void
}) {
  const { t } = useTranslation()
  const toggle = useToggleSchedule()
  const run = useRunSchedule()
  const remove = useDeleteSchedule()
  const confirm = useConfirm()
  const summary = useScheduleSummary()

  const runNow = (s: Schedule) => fire(run.mutateAsync(s).then(() => onHistory(s)))

  const columns = useMemo(
    () =>
      createColumns<Schedule>((c) => [
        c.accessor("name", {
          header: t("studio.schedules.columns.schedule"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="max-w-sm min-w-44">
              <div className="truncate font-medium">{row.original.name}</div>
              <div className="text-muted-foreground truncate text-xs">
                {row.original.description || t("studio.schedules.noDescription")}
              </div>
            </div>
          ),
        }),
        c.display({
          id: "timing",
          header: t("studio.schedules.columns.timing"),
          cell: ({ row }) => (
            <div className="max-w-64 min-w-40">
              <div className="truncate text-[0.8125rem]">{summary(row.original)}</div>
              <div className="text-muted-foreground truncate text-xs">
                {row.original.cron && <code className="mr-1.5 font-mono">{row.original.cron}</code>}
                {row.original.effective_timezone}
              </div>
            </div>
          ),
        }),
        c.display({
          id: "actions",
          header: t("studio.schedules.columns.actions"),
          cell: ({ row }) => {
            const a = row.original.actions
            const first = a[0]
            return (
              <div className="flex max-w-56 min-w-32 flex-wrap items-center gap-1">
                {first && (
                  <StatusBadge className="max-w-44">
                    <span className="truncate">
                      {first.target_name ??
                        t("studio.schedules.missingTarget", {
                          id:
                            first.type === "agent_automation"
                              ? first.automation_id
                              : first.team_automation_id,
                        })}
                    </span>
                  </StatusBadge>
                )}
                {a.length > 1 && (
                  <span className="text-muted-foreground text-xs">
                    {t("studio.schedules.moreActions", { count: a.length - 1 })}
                  </span>
                )}
              </div>
            )
          },
        }),
        c.accessor("next_run_at", {
          header: t("studio.schedules.columns.next"),
          sortFn: "datetime",
          cell: ({ row }) =>
            row.original.enabled ? (
              <RelativeTime
                value={row.original.next_run_at}
                fallback={t("studio.schedules.notPlanned")}
                className="text-[0.8125rem]"
              />
            ) : (
              <StatusBadge>{t("studio.schedules.paused")}</StatusBadge>
            ),
        }),
        c.accessor("last_run_at", {
          header: t("studio.schedules.columns.last"),
          sortFn: "datetime",
          cell: ({ row }) => {
            const s = row.original
            return (
              <div className="flex items-center gap-1.5">
                {s.last_success != null && (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span
                          className={
                            s.last_success
                              ? "bg-success size-1.5 shrink-0 rounded-full"
                              : "bg-destructive size-1.5 shrink-0 rounded-full"
                          }
                          role="img"
                          aria-label={
                            s.last_success ? t("studio.schedules.lastOk") : t("studio.schedules.lastFailed")
                          }
                        />
                      }
                    />
                    <TooltipContent className="max-w-xs">
                      {s.last_success
                        ? t("studio.schedules.lastOk")
                        : s.last_error || t("studio.schedules.lastFailed")}
                    </TooltipContent>
                  </Tooltip>
                )}
                <RelativeTime value={s.last_run_at} className="text-muted-foreground text-[0.8125rem]" />
              </div>
            )
          },
        }),
        c.accessor("enabled", {
          header: t("studio.schedules.columns.enabled"),
          meta: { align: "end" },
          cell: ({ row }) => (
            <div
              className="flex justify-end"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Switch
                size="sm"
                checked={row.original.enabled}
                onCheckedChange={(v) => toggle.mutate({ id: row.original.id, enabled: v })}
                aria-label={t("studio.schedules.toggle", { name: row.original.name })}
              />
            </div>
          ),
        }),
      ]),
    [t, toggle.mutate, summary],
  )

  return (
    <DataTable
      label={t("studio.schedules.title")}
      data={schedules.data}
      columns={columns}
      getRowId={(s) => String(s.id)}
      loading={schedules.isPending}
      error={schedules.error}
      onRetry={() => void schedules.refetch()}
      searchable
      searchPlaceholder={t("studio.schedules.search")}
      initialSorting={[{ id: "name", desc: false }]}
      onRowClick={onEdit}
      rowActions={(s) => (
        <>
          <DropdownMenuItem onClick={() => onEdit(s)}>
            <Pencil />
            {t("common.edit")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => runNow(s)} disabled={s.actions.length === 0}>
            <Play />
            {t("studio.schedules.runNow")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onHistory(s)}>
            <History />
            {t("studio.schedules.history")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() =>
              void confirm({
                title: t("studio.schedules.deleteTitle", { name: s.name }),
                description: t("studio.schedules.deleteDescription"),
                confirmLabel: t("studio.schedules.delete"),
                destructive: true,
                onConfirm: () => remove.mutateAsync(s),
              })
            }
          >
            <Trash2 />
            {t("studio.schedules.delete")}
          </DropdownMenuItem>
        </>
      )}
      emptyState={
        <EmptyState
          variant="plain"
          icon={CalendarClock}
          title={t("studio.schedules.empty.title")}
          description={t("studio.schedules.empty.description")}
          action={
            <Button size="sm" onClick={onCreate}>
              <Plus />
              {t("studio.schedules.new")}
            </Button>
          }
        />
      }
    />
  )
}
